import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from '../../styles/tenant.module.css';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useSubscription } from '@apollo/client';
import {
  CREATE_TENANT,
  LOAD_TENANTS,
  TENANT_STATUS_SUBSCRIPTION,
} from '../../graphql/TenantQueries';
import ErrorDisplay from '../common/ErrorDisplay';
import StatusDisplay from '../common/StatusDisplay';
import FeatureConfigurationSection from '../common/FeatureConfigurationSection';
import {
  TenantFormData,
  AddTenantModalProps,
  Feature,
  UseTenantStatus,
} from '../../types/tenant';
import { processFormData, formatTenantName } from '../../utils/formUtils';
import { useSocketTenantStatus } from '../../hooks/useSocketTenantStatus';
import { useSession } from 'next-auth/react';
import getConfig from 'next/config';
import logger from '../../utils/logger';

const { publicRuntimeConfig } = getConfig();

function AddTenantModal({ setOpenModal, graphqlApi }: AddTenantModalProps) {
  const { data: session }: any = useSession();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    trigger,
  } = useForm<TenantFormData>({
    mode: 'onChange',
  });
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<
    Record<number, Feature[]>
  >({});
  // Enhanced type definition for tenant status
  const [tenantStatus, setTenantStatus] = useState<UseTenantStatus>({
    status: 'IDLE',
    message: '',
    detailedMessages: [] as const,
  });
  const [processComplete, setProcessComplete] = useState(false);
  const [automationComplete, setAutomationComplete] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [socketIOFailed, setSocketIOFailed] = useState(false); // Start with Socket.IO by default
  const [useSocketIO, setUseSocketIO] = useState(true); // Socket.IO as primary option

  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showDetails, setShowDetails] = useState(true); // Show details by default to see Socket.IO updates
  // Socket.IO tenant status updates - derive URL from GraphQL API prop
  const socketUrl = graphqlApi
    ? graphqlApi.replace('/graphql', '')
    : 'http://localhost:3001';

  const { socketConnected } = useSocketTenantStatus({
    tenantStatus,
    setTenantStatus,
    socketUrl,
    token: session?.accessToken,
    enabled: useSocketIO && !!session?.accessToken, // Only enabled when user selects Socket.IO
  });

  // Monitor Socket.IO health when enabled
  useEffect(() => {
    if (!useSocketIO) return;

    const timer = setTimeout(() => {
      if (tenantStatus.status !== 'IDLE' && !socketConnected) {
        logger.warn('Socket.IO failed, switching back to GraphQL', {
          tenantStatus: tenantStatus.status,
          socketConnected,
        });
        setUseSocketIO(false);
        setSocketIOFailed(true);
      }
    }, 10000); // Wait 10 seconds when Socket.IO is enabled

    return () => clearTimeout(timer);
  }, [tenantStatus.status, socketConnected, useSocketIO]);

  const [addTenant, { loading, error: mutationError }] = useMutation(
    CREATE_TENANT,
    {
      refetchQueries: [{ query: LOAD_TENANTS }, 'getTenants'],
    }
  );

  // Memoize subscription variables to prevent unnecessary re-subscriptions
  // Listen to all tenant updates without filtering by ID to catch temp IDs
  const subscriptionVariables = useMemo(() => ({}), []);

  // Optimize subscription data handler to reduce re-renders
  const handleSubscriptionData = useCallback(
    ({ subscriptionData }) => {
      if (subscriptionData.data?.tenantStatusUpdates) {
        const update = subscriptionData.data.tenantStatusUpdates;

        logger.info('Received subscription update', {
          update: {
            tenantId: update.tenantId,
            status: update.status,
            message: update.message,
          },
        });

        setTenantStatus((prev) => {
          // Filter updates for this specific tenant creation session
          const isRelevantUpdate =
            update.tenantId === prev.createdTenantId ||
            update.tenantId === prev.tempTenantId ||
            update.tenantName === prev.createdTenantName;

          if (!isRelevantUpdate) {
            logger.debug('Ignoring irrelevant update', {
              updateTenantId: update.tenantId,
              currentTenantId: prev.createdTenantId,
              tempTenantId: prev.tempTenantId,
              updateTenantName: update.tenantName,
              currentTenantName: prev.createdTenantName,
            });
            return prev;
          }

          const timestamp = new Date().toLocaleTimeString();
          const newDetailedMessages = [...(prev.detailedMessages || [])];
          newDetailedMessages.push(
            `[${timestamp}] ${update.message || update.status}`
          );

          if (newDetailedMessages.length > 10) {
            newDetailedMessages.shift();
          }

          // Use the logic from the hook instead of duplicating
          const overallStatus = update.status;

          const updatedStatus = {
            ...prev,
            status: overallStatus,
            message: update.message || `Status: ${update.status}`,
            detailedMessages: newDetailedMessages,
            automationStarted:
              update.status !== 'IDLE' || prev.automationStarted,
          };

          // Track temp ID for IN_PROGRESS updates
          if (update.tenantId?.startsWith('temp-') && !prev.tempTenantId) {
            updatedStatus.tempTenantId = update.tenantId;
          }

          return updatedStatus;
        });

        // Clear any existing auto-close timeout
        if (autoCloseTimeoutRef.current) {
          clearTimeout(autoCloseTimeoutRef.current);
        }

        // Check if this is the final completion status
        if (update.status === 'SUCCESS') {
          // Look for completion indicators in the message
          const isCompleteMessage =
            update.message &&
            (update.message.toLowerCase().includes('completed') ||
              update.message.toLowerCase().includes('finished') ||
              update.message.toLowerCase().includes('done') ||
              update.message.toLowerCase().includes('automation complete') ||
              update.message.toLowerCase().includes('process complete'));

          if (isCompleteMessage) {
            setProcessComplete(true);
            // Auto-close modal after 3 seconds when process is complete
            autoCloseTimeoutRef.current = setTimeout(() => {
              setOpenModal(false);
            }, 3000);
          } else {
            // Auto-close after 5 seconds for SUCCESS status even if not explicitly "complete"
            autoCloseTimeoutRef.current = setTimeout(() => {
              setOpenModal(false);
            }, 5000);
          }
        } else if (update.status === 'FAILURE') {
          setProcessComplete(true);
          setShowCloseButton(true);
        }
      }
    },
    [setOpenModal]
  );

  const handleSubscriptionError = useCallback((error) => {
    // Only show subscription errors as failures if we're in the middle of creating a tenant
    setTenantStatus((prev) => {
      if (prev.status === 'IDLE') {
        // Don't show connection errors as failures when modal first loads
        logger.warn('GraphQL subscription error during idle state', {
          error: error.message,
          stack: error.stack,
        });
        return prev;
      }

      return {
        ...prev,
        status: 'FAILURE',
        message: `Subscription error: ${error.message}`,
        detailedMessages: [
          ...(prev.detailedMessages || []),
          `[${new Date().toLocaleTimeString()}] Subscription error: ${
            error.message
          }`,
        ],
      };
    });
  }, []);

  // GraphQL subscription as fallback when Socket.IO fails
  const {
    data: statusData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription(TENANT_STATUS_SUBSCRIPTION, {
    variables: subscriptionVariables,
    skip: !socketIOFailed, // Only enable when Socket.IO has failed
    onSubscriptionData: handleSubscriptionData,
    onError: handleSubscriptionError,
  });

  // Function to detect if BOTH automation processes are complete
  const detectAutomationCompletion = useCallback((tenantStatusState) => {
    const { clientAutomation, dbAutomation } = tenantStatusState;

    const clientComplete =
      clientAutomation?.status &&
      (clientAutomation.status === 'SUCCESS' ||
        clientAutomation.status === 'FAILURE');
    const dbComplete =
      dbAutomation?.status &&
      (dbAutomation.status === 'SUCCESS' || dbAutomation.status === 'FAILURE');

    return clientComplete && dbComplete;
  }, []);

  // Check for automation completion on each status update
  useEffect(() => {
    if (detectAutomationCompletion(tenantStatus)) {
      setAutomationComplete(true);
    }
  }, [tenantStatus, detectAutomationCompletion]);

  // Handle completion status from Socket.IO updates
  useEffect(() => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    if (tenantStatus.status === 'SUCCESS' && automationComplete) {
      // Only mark as complete when automation is fully done
      setProcessComplete(true);
      setShowCloseButton(true);
      autoCloseTimeoutRef.current = setTimeout(() => {
        setOpenModal(false);
      }, 3000);
    } else if (tenantStatus.status === 'SUCCESS' && !automationComplete) {
      // Tenant created but automation not complete yet
      setProcessComplete(false);
      setShowCloseButton(false);
    } else if (tenantStatus.status === 'FAILURE') {
      setProcessComplete(true);
      setShowCloseButton(true);
      // Modal stays open - let user manually close or retry
    } else if (tenantStatus.status === 'IN_PROGRESS') {
      setProcessComplete(false);
      setShowCloseButton(false);
    }
  }, [
    tenantStatus.status,
    tenantStatus.message,
    automationComplete,
    setOpenModal,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, []);
  // Memoize field arrays to reduce re-renders
  const tagFieldArray = useFieldArray({ control, name: 'tags' });
  const pwsFieldArray = useFieldArray({ control, name: 'publicWaterSystems' });
  const contactFieldArray = useFieldArray({ control, name: 'contacts' });
  const appFieldArray = useFieldArray({
    control,
    name: 'applicationSubscriptions',
    rules: { required: 'At least one application subscription is required' },
  });
  const aliasFieldArray = useFieldArray({ control, name: 'aliases' });

  const {
    fields: tagFields,
    append: tagAppend,
    remove: tagRemove,
  } = tagFieldArray;
  const {
    fields: pwsFields,
    append: pwsAppend,
    remove: pwsRemove,
  } = pwsFieldArray;
  const {
    fields: contactFields,
    append: contactAppend,
    remove: contactRemove,
  } = contactFieldArray;
  const {
    fields: appFields,
    append: appAppend,
    remove: appRemove,
  } = appFieldArray;
  const {
    fields: aliasFields,
    append: aliasAppend,
    remove: aliasRemove,
  } = aliasFieldArray;
  const handleNameBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setValue('name', formatTenantName(event.target.value));
    },
    [setValue]
  );
  // Reset tenant status for retry attempts
  const resetTenantStatus = useCallback(() => {
    setTenantStatus({
      status: 'IDLE',
      message: '',
      detailedMessages: [],
      automationStarted: false,
    });
    setShowDetails(true); // Keep details shown
    setProcessComplete(false);
    setAutomationComplete(false);
    setShowCloseButton(false);
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
    }
  }, []);

  const onSubmit = async (formData: TenantFormData) => {
    if (
      !formData.applicationSubscriptions ||
      formData.applicationSubscriptions.length === 0
    ) {
      setValue('applicationSubscriptions', []);
      trigger('applicationSubscriptions');
      return;
    }

    // If this is a retry attempt, reset the status first
    if (tenantStatus.status === 'FAILURE') {
      resetTenantStatus();
      // Add a small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    try {
      // Reset status and start tenant creation
      setTenantStatus({
        status: 'IN_PROGRESS',
        message: 'Initializing tenant creation...',
        detailedMessages: [
          `[${new Date().toLocaleTimeString()}] Starting tenant creation process`,
        ],
        automationStarted: false,
      });

      const subscriptions = formData.applicationSubscriptions || [];

      subscriptions.forEach((subscription: any, index: number) => {
        const featuresFromState = subscriptionFeatures[index] || [];

        if (featuresFromState.length > 0) {
          const cleanedFeatures = featuresFromState.map((feature) => ({
            name: feature.name || '',
            description: feature.description,
            enabled: Boolean(feature.enabled),
            options: feature.options || {},
          }));

          subscription.features = cleanedFeatures
            .map((feature) => JSON.stringify(feature))
            .join(',');
        } else {
          subscription.features = '';
        }
      });

      const processedData = processFormData(formData);

      // Update status before making the API call
      setTenantStatus((prev) => ({
        ...prev,
        message: 'Creating tenant in database...',
        detailedMessages: [
          ...(prev.detailedMessages || []),
          `[${new Date().toLocaleTimeString()}] Sending tenant data to API`,
        ],
      }));

      const result = await addTenant({ variables: { input: processedData } });

      // Set the created tenant ID and name to listen for status updates
      if (result.data?.addTenant?.id) {
        setTenantStatus((prev) => ({
          ...prev,
          createdTenantId: result.data.addTenant.id,
          createdTenantName: result.data.addTenant.name || processedData.name,
          message:
            'Tenant created in database. Waiting for automation status...',
          detailedMessages: [
            ...(prev.detailedMessages || []),
            `[${new Date().toLocaleTimeString()}] Tenant created with ID: ${
              result.data.addTenant.id
            }`,
            `[${new Date().toLocaleTimeString()}] Listening for automation updates...`,
          ],
        }));
      } else {
        throw new Error('Tenant creation succeeded but no ID was returned');
      }
    } catch (err) {
      logger.error('Error creating tenant', {
        error: err.message,
        stack: err.stack,
        tenantName: processedData?.name,
      });
      setTenantStatus({
        status: 'FAILURE',
        message: err.message || 'Failed to create tenant',
        detailedMessages: [
          `[${new Date().toLocaleTimeString()}] Error: ${
            err.message || 'Unknown error occurred'
          }`,
        ],
      });
    }
  };

  return (
    <div className={styles['modalBackground']}>
      <div className={styles['formModalContainer']}>
        <div className={styles['title']}>
          <h1>Add Tenant</h1>
        </div>
        <hr></hr>

        <div className={styles['body']}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className={styles['label']}>Tenant Name *</label>
              <input
                {...register('name', { required: 'Tenant name is required' })}
                className={styles['input']}
                onBlur={handleNameBlur}
              />
              {errors.name && (
                <div
                  style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}
                >
                  {errors.name.message}
                </div>
              )}
            </div>
            <div>
              <label className={styles['label']}>Display Name</label>
              <input
                {...register('displayName')}
                className={styles['input']}
              ></input>
            </div>
            <div>
              <label className={styles['label']}>Timezone</label>
              <select {...register(`timezone`)} className={styles['input']}>
                <option value="America/New_York" id="1" key="America/New_York">
                  {' '}
                  America/New York
                </option>
                <option value="America/Chicago" id="2" key="America/Chicago">
                  {' '}
                  America/Chicago
                </option>
                <option
                  value="America/Los_Angeles"
                  id="3"
                  key="America/Los_Angeles"
                >
                  {' '}
                  America/Los Angeles
                </option>
                <option value="America/Alaska" id="3" key="America/Alaska">
                  {' '}
                  America/Alaska
                </option>
                <option value="America/Arizona" id="3" key="America/Arizona">
                  {' '}
                  America/Arizona
                </option>
                <option value="America/Hawaii" id="3" key="America/Hawaii">
                  {' '}
                  America/Hawaii
                </option>
              </select>
            </div>
            <hr />
            <h3 style={{ display: 'flex', paddingTop: '20px' }}>Contacts</h3>
            {contactFields.map((field, index) => {
              return (
                <div key={index}>
                  <label className={styles['label']}>Contact Name</label>
                  <input
                    {...register(`contacts.${index}.name`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>Contact Email</label>
                  <input
                    {...register(`contacts.${index}.email`)}
                    className={styles['input']}
                    type="email"
                  ></input>
                  <label className={styles['label']}>Contact Website</label>
                  <input
                    {...register(`contacts.${index}.website`)}
                    className={styles['input']}
                  ></input>
                  <div>
                    <label className={styles['label']}>Contact Phone</label>
                    <input
                      {...register(`contacts.${index}.phone`)}
                      className={styles['input']}
                    ></input>
                    <label className={styles['label']}>Contact Type</label>
                    <input
                      {...register(`contacts.${index}.type`)}
                      className={styles['input']}
                    ></input>
                    <label className={styles['label']}>Contact Address</label>
                    <input
                      {...register(`contacts.${index}.address`)}
                      className={styles['input']}
                    ></input>
                  </div>
                  <div>
                    <label className={styles['label']}>
                      Contact Lead Website
                    </label>
                    <input
                      {...register(`contacts.${index}.leadWebsite`)}
                      className={styles['input']}
                    ></input>
                  </div>

                  <button
                    type="button"
                    onClick={() => contactRemove(index)}
                    className={styles['cnclButton']}
                    style={{ width: '100px', height: '35px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                  <hr />
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => contactAppend({})}
              className={styles['button']}
              style={{ width: '150px', height: '35px', fontSize: '14px' }}
            >
              Add Contact
            </button>
            <hr />
            <h3 style={{ display: 'flex', paddingTop: '20px' }}>Aliases</h3>
            {aliasFields.map((field, index) => {
              return (
                <div key={index}>
                  <label className={styles['label']}>Alias</label>
                  <input
                    {...register(`aliases.${index}.alias`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>Description</label>
                  <input
                    {...register(`aliases.${index}.description`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>
                    Additional Parameters
                  </label>
                  <input
                    {...register(`aliases.${index}.additionalParameters`)}
                    className={styles['input']}
                  ></input>
                  <button
                    type="button"
                    onClick={() => aliasRemove(index)}
                    className={styles['cnclButton']}
                    style={{ width: '100px', height: '35px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                  <hr />
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => aliasAppend({})}
              className={styles['button']}
              style={{ width: '150px', height: '35px', fontSize: '14px' }}
            >
              Add Alias
            </button>
            <hr />
            <h3 style={{ display: 'flex', paddingTop: '20px' }}>
              Application Subscriptions *
            </h3>
            {errors.applicationSubscriptions && (
              <div
                style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}
              >
                {errors.applicationSubscriptions.message ||
                  'At least one application subscription is required'}
              </div>
            )}
            {appFields.map((field, index) => {
              return (
                <div key={index}>
                  <label className={styles['label']}>Application</label>
                  <select
                    className={styles['input']}
                    style={{ marginRight: '10px' }}
                    {...register(
                      `applicationSubscriptions.${index}.application`
                    )}
                  >
                    <option value="leadCAST" id="1" key="leadCAST">
                      {' '}
                      leadCAST
                    </option>
                    <option value="pipeCAST" id="2" key="pipeCAST">
                      {' '}
                      pipeCAST
                    </option>
                    <option value="epiCAST" id="3" key="epiCAST">
                      {' '}
                      epiCAST
                    </option>
                    <option value="proteus" id="4" key="proteus">
                      {' '}
                      Raini
                    </option>
                    <option value="assetCAST" id="5" key="assetCAST">
                      {' '}
                      assetCAST
                    </option>
                    <option value="datacast" id="6" key="datacast">
                      {' '}
                      dataCAST
                    </option>
                  </select>
                  <label className={styles['label']}>License Level</label>
                  <input
                    {...register(
                      `applicationSubscriptions.${index}.licenseLevel`
                    )}
                    className={styles['input']}
                  />
                  <FeatureConfigurationSection
                    value={subscriptionFeatures[index] || []}
                    onChange={(features) => {
                      // Process template placeholders in features
                      setSubscriptionFeatures((prev) => ({
                        ...prev,
                        [index]: features,
                      }));
                    }}
                    applicationName={
                      watch(`applicationSubscriptions.${index}.application`) ||
                      'leadCAST'
                    }
                    showApplicationSelector={false}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      appRemove(index);
                      // Clean up features state for this subscription
                      setSubscriptionFeatures((prev) => {
                        const newState = { ...prev };
                        delete newState[index];
                        // Reindex remaining items
                        const reindexed: { [key: number]: any[] } = {};
                        Object.keys(newState).forEach((key) => {
                          const keyNum = parseInt(key);
                          if (keyNum > index) {
                            reindexed[keyNum - 1] = newState[keyNum];
                          } else {
                            reindexed[keyNum] = newState[keyNum];
                          }
                        });
                        return reindexed;
                      });
                    }}
                    className={styles['cnclButton']}
                    style={{ width: '100px', height: '35px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                  <hr />
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => appAppend({})}
              className={styles['button']}
              style={{ width: '200px', height: '35px', fontSize: '14px' }}
            >
              Add App Subscription
            </button>
            <hr />
            <h3 style={{ display: 'flex', paddingTop: '20px' }}>Tags</h3>
            {tagFields.map((field, index) => {
              return (
                <div key={index}>
                  <label className={styles['label']}>Name</label>
                  <input
                    {...register(`tags.${index}.name`)}
                    className={styles['input']}
                  />
                  <label className={styles['label']}>Value</label>
                  <input
                    {...register(`tags.${index}.value`)}
                    className={styles['input']}
                  />
                  <button
                    type="button"
                    onClick={() => tagRemove(index)}
                    className={styles['cnclButton']}
                    style={{ width: '100px', height: '35px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                  <hr />
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => tagAppend({})}
              className={styles['button']}
              style={{ width: '150px', height: '35px', fontSize: '14px' }}
            >
              Add Tags
            </button>
            <hr />
            <h3 style={{ display: 'flex', paddingTop: '20px' }}>
              Public Water Systems
            </h3>
            {pwsFields.map((field, index) => {
              return (
                <div key={index}>
                  <label className={styles['label']}>PWS ID</label>
                  <input
                    {...register(`publicWaterSystems.${index}.pwsId`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>Alias</label>
                  <input
                    {...register(`publicWaterSystems.${index}.alias`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>Name</label>
                  <input
                    {...register(`publicWaterSystems.${index}.name`)}
                    className={styles['input']}
                  ></input>
                  <label className={styles['label']}>Begin Date</label>
                  <input
                    {...register(`publicWaterSystems.${index}.beginOn`)}
                    className={styles['input']}
                    type="date"
                  ></input>
                  <button
                    type="button"
                    onClick={() => pwsRemove(index)}
                    className={styles['cnclButton']}
                    style={{ width: '100px', height: '35px', fontSize: '14px' }}
                  >
                    Delete
                  </button>
                  <hr />
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => pwsAppend({})}
              className={styles['button']}
              style={{ width: '250px', height: '35px', fontSize: '14px' }}
            >
              Add Public Water System
            </button>
            <hr />

            <h3 style={{ display: 'flex', paddingTop: '20px' }}>Location</h3>
            <label className={styles['label']}>Latitude</label>
            <input
              {...register('location.latitude')}
              className={styles['input']}
              type="number"
              step="any"
            ></input>
            <label className={styles['label']}>Longitude</label>
            <input
              {...register('location.longitude')}
              className={styles['input']}
              type="number"
              step="any"
            ></input>
            <div>
              <label className={styles['label']}>Zoom</label>
              <input
                {...register('location.zoom')}
                className={styles['input']}
                type="number"
                step="any"
              ></input>
            </div>

            {/* Enhanced Status Display */}
            <StatusDisplay
              status={tenantStatus.status}
              message={tenantStatus.message}
              createdTenantName={tenantStatus.createdTenantName}
              createdTenantId={tenantStatus.createdTenantId}
              automationStarted={tenantStatus.automationStarted}
              detailedMessages={tenantStatus.detailedMessages}
              showDetails={showDetails}
              setShowDetails={setShowDetails}
              clientAutomation={tenantStatus.clientAutomation}
              dbAutomation={tenantStatus.dbAutomation}
            />

            {/* Connection Method Selection & Status */}
            {tenantStatus.status !== 'IDLE' && (
              <div
                style={{
                  fontSize: '12px',
                  marginBottom: '10px',
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                }}
              ></div>
            )}

            <div>
              <button
                type="button"
                onClick={() => {
                  // Cancel any pending auto-close when user manually closes
                  if (autoCloseTimeoutRef.current) {
                    clearTimeout(autoCloseTimeoutRef.current);
                  }
                  setOpenModal(false);
                }}
                className={styles['button']}
              >
                {tenantStatus.status === 'IN_PROGRESS' && showCloseButton
                  ? 'Force Close'
                  : tenantStatus.status === 'SUCCESS' && processComplete
                  ? 'Close'
                  : 'Cancel'}
              </button>

              <input
                type="submit"
                className={styles['button']}
                value={
                  tenantStatus.status === 'SUCCESS' && automationComplete
                    ? '✅ Success!'
                    : tenantStatus.status === 'FAILURE'
                    ? '🔄 Retry'
                    : 'Create Tenant'
                }
                disabled={
                  loading ||
                  tenantStatus.status === 'IN_PROGRESS' ||
                  (tenantStatus.status === 'SUCCESS' && !automationComplete) ||
                  (tenantStatus.status === 'SUCCESS' && automationComplete)
                }
                style={{
                  backgroundColor:
                    tenantStatus.status === 'SUCCESS' && automationComplete
                      ? '#28a745'
                      : tenantStatus.status === 'FAILURE'
                      ? '#dc3545'
                      : tenantStatus.status === 'IN_PROGRESS' ||
                        (tenantStatus.status === 'SUCCESS' &&
                          !automationComplete)
                      ? '#17a2b8'
                      : undefined,
                  color: tenantStatus.status !== 'IDLE' ? 'white' : undefined,
                  opacity:
                    loading ||
                    tenantStatus.status === 'IN_PROGRESS' ||
                    (tenantStatus.status === 'SUCCESS' &&
                      !automationComplete) ||
                    (tenantStatus.status === 'SUCCESS' && automationComplete)
                      ? 0.7
                      : 1,
                }}
              />
            </div>
          </form>
        </div>
        <ErrorDisplay error={mutationError} />
        <div className={styles['footer']}></div>
      </div>
    </div>
  );
}

export default AddTenantModal;
